/**
 * Paths the native apps never handle themselves. Keep in sync with the exclusions in
 * `app-portal/public/apple-app-site-association`, `app-portal/public/.well-known/assetlinks.json`
 * and the `<uri-relative-filter-group>` entries in the Android manifest.
 *
 * - `/checkout`: checkout is web-only (App Store rules); "Buy Hours" is hidden on native and every
 *   other in-app link to it lands on the "not available in the app" notice.
 * - `/agbs`: static terms-and-conditions PDFs served from the web host; the app cannot render them.
 */
export const WEB_ONLY_PATH_PREFIXES: readonly string[] = ['/checkout', '/agbs'];

/**
 * True when a path (or full URL) belongs to a web-only area. Matches the prefix as a whole path
 * segment, so `/checkout` and `/checkout/success/1?x=y` match but `/checkouts` or
 * `/profile?next=/checkout` do not. Case-insensitive, query string and fragment are ignored.
 */
export function isWebOnlyPath(pathOrUrl: string | null | undefined): boolean {
    const pathname = extractPathname(pathOrUrl);
    if (!pathname) {
        return false;
    }

    return WEB_ONLY_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
}

function extractPathname(pathOrUrl: string | null | undefined): string | null {
    if (!pathOrUrl) {
        return null;
    }

    let pathname = pathOrUrl.trim();
    if (/^[a-z][a-z0-9+.-]*:/i.test(pathname)) {
        try {
            pathname = new URL(pathname).pathname;
        } catch {
            return null;
        }
    }

    pathname = pathname.split(/[?#]/)[0].toLowerCase();
    if (!pathname.startsWith('/')) {
        pathname = '/' + pathname;
    }

    return pathname.replace(/\/{2,}/g, '/');
}
